import { cva, type VariantProps } from 'class-variance-authority';
import * as Slot from '@radix-ui/react-slot';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * EDS Button Group — see https://registry.eds.dev.access-evo.com/components/base/button-group.
 *
 * Groups buttons together with consistent styling: strips the rounding
 * and inner border between adjacent children so the group reads as a
 * single segmented control. Selection / toggle state is the caller's
 * responsibility; this component only handles the visual grouping.
 */
const buttonGroupVariants = cva(
  "flex w-fit items-stretch has-[>[data-slot=button-group]]:gap-2 [&>*]:focus-visible:relative [&>*]:focus-visible:z-10 has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1",
  {
    variants: {
      orientation: {
        horizontal:
          '[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:last-child)]:rounded-r-none',
        vertical:
          'flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:last-child)]:rounded-b-none',
      },
    },
    defaultVariants: {
      orientation: 'horizontal',
    },
  },
);

/** Visually groups child buttons into a single segmented control. */
function ButtonGroup({
  className,
  orientation,
  ...props
}: Readonly<React.ComponentProps<'div'> & VariantProps<typeof buttonGroupVariants>>) {
  return (
    <div
      role="group"
      data-slot="button-group"
      data-orientation={orientation}
      className={cn(buttonGroupVariants({ orientation }), className)}
      {...props}
    />
  );
}

/** Static text slot that sits between buttons in the group. */
function ButtonGroupText({
  className,
  asChild = false,
  ...props
}: Readonly<
  React.ComponentProps<'div'> & {
    asChild?: boolean;
  }
>) {
  const Comp = asChild ? Slot.Root : 'div';

  return (
    <Comp
      data-slot="button-group-text"
      className={cn(
        "bg-muted shadow-xs flex items-center gap-2 rounded-md border px-4 text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

/** Thin rule drawn between adjacent buttons in the group. */
function ButtonGroupSeparator({
  className,
  orientation = 'vertical',
  ...props
}: Readonly<React.ComponentProps<typeof Separator>>) {
  return (
    <Separator
      data-slot="button-group-separator"
      orientation={orientation}
      className={cn(
        'bg-input data-[orientation=vertical]:h-auto relative m-0! self-stretch',
        className,
      )}
      {...props}
    />
  );
}

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText, buttonGroupVariants };
